import { DeliveryAddress } from 'state/delivery';
import { IconDelivery, IconPeoplePartners, IconTicket, IconToGo } from '@tock/icons';
import { IconDelivery as IconDeliveryLegacy, IconDeliveryScooter, IconPeople } from 'ui/icons';
import { SlimPatron, getFullName } from '@tock/shared/utils/patron';
import { StoreType } from '@tock/shared/utils/connect';
import { core, safeEnumCompare } from '@tock/coretypes';
import {
  deliveryServiceProviderDisplayName,
  isEventVariety,
  offeringPartyItem,
} from '@tock/shared/utils/offerings';
import { pluralize } from '@tock/shared/utils/pluralize';
import _ from 'lodash';
import protos from 'utils/protos';

const { ExtraChargeDetails } = protos;

// Sets the payment card field on the purchase order based on whether we're accepting multiple processor card tokens.
export function addPaymentCardOrCards(
  purchaseOrder: core.IPurchaseOrder,
  cardOrCards: core.IPaymentCard[] | core.IPaymentCard,
  deviceData?: string
) {
  if (_.isArray(cardOrCards)) {
    // When accepting multiple payment tokens, this will be an array of card tokens.
    purchaseOrder.paymentCards = _.map(cardOrCards, (card) => ({
      ...card,
      deviceData,
    }));
  } else {
    purchaseOrder.paymentCard = {
      ...cardOrCards,
      deviceData,
    };
  }
}

export function addPatron(purchaseOrder: core.IPurchaseOrder, patron: core.IPatron) {
  purchaseOrder.patron = patron;
}

export function isPurchaseOfEvent(pastPurchase: core.PastPurchase) {
  return isEventVariety(pastPurchase.ticketTypeVariety);
}

export function addDeliveryInfo(
  purchaseOrder: core.IPurchaseOrder,
  deliveryAddress: DeliveryAddress,
  patron?: core.IPatron
) {
  const delivery: core.IDelivery = {
    location: deliveryAddress.address,
    phoneCountryCode: patron?.phoneCountryCode,
    phone: patron?.phone,
    deliveryInstructions: deliveryAddress.deliveryNotes,
  };
  purchaseOrder.delivery = delivery;
}

export function getGuestsIcon(
  ticketTypeVariety: core.TicketType.Variety | undefined,
  { useSakeIcons }: { useSakeIcons: boolean },
  ticketTypeDescriptiveVariety?: core.DescriptiveVariety,
  deliveryServiceProvider?: core.DeliveryServiceProvider
) {
  let icon = useSakeIcons ? IconPeoplePartners : IconPeople;
  switch (ticketTypeDescriptiveVariety) {
    case 'TOGO_PACKAGE':
      icon = useSakeIcons ? IconDelivery : IconDeliveryLegacy;
      break;
  }

  switch (ticketTypeVariety) {
    case 'GA_EVENT':
    case 'SEATED_EVENT':
      icon = useSakeIcons ? IconTicket : IconPeople;
      break;
    case 'ORDER_DELIVERY':
      if (deliveryServiceProvider === 'INTERNAL') {
        icon = useSakeIcons ? IconDelivery : IconDeliveryLegacy;
      } else {
        icon = useSakeIcons ? IconDelivery : IconDeliveryScooter;
      }
      break;
    case 'ORDER_PICKUP':
      icon = useSakeIcons ? IconToGo : IconDeliveryLegacy;
      break;
  }
  return icon;
}

export function getGuestsLine(
  ticketTypeVariety: core.TicketType.Variety | undefined,
  count: number,
  owner?: SlimPatron,
  isAuthenticated?: boolean,
  ticketTypeDescriptiveVariety?: core.DescriptiveVariety,
  deliveryServiceProvider?: core.DeliveryServiceProvider,
  singleUnitQuantity?: number
) {
  const partySizeUnit = ticketTypeVariety
    ? offeringPartyItem(ticketTypeVariety, ticketTypeDescriptiveVariety ?? undefined)
    : 'guest';
  let purchaseOwnerText = '';
  let deliveryPickupPrefix = '';
  let guestsLine = `${pluralize(count, partySizeUnit)}`;
  let forLine = '';

  // First switch on variety and make general changes
  switch (ticketTypeVariety) {
    case 'ORDER_DELIVERY':
      if (deliveryServiceProvider === 'INTERNAL' || deliveryServiceProvider === undefined) {
        deliveryPickupPrefix = `Delivery`;
      } else {
        deliveryPickupPrefix = `${deliveryServiceProviderDisplayName(
          deliveryServiceProvider
        )} delivery`;
      }
      guestsLine = '';
      break;
    case 'ORDER_PICKUP':
      deliveryPickupPrefix = `Pickup`;
      guestsLine = '';
      break;
    default:
      purchaseOwnerText =
        isAuthenticated || !owner || getFullName(owner) === '-' ? '' : ` for ${getFullName(owner)}`;
      break;
  }

  // Then switch on descriptive variety and make more specific changes if needed
  switch (ticketTypeDescriptiveVariety) {
    case 'TOGO_PACKAGE':
      forLine = deliveryPickupPrefix ? ' of ' : '';
      guestsLine = `${pluralize(count, partySizeUnit)}${
        singleUnitQuantity !== undefined
          ? ` (serves ${singleUnitQuantity}${
              singleUnitQuantity !== undefined && count > 1 ? ' each' : ''
            })`
          : ''
      }`;
      break;
    case 'TOGO_PRIX_FIXE':
      forLine = deliveryPickupPrefix ? ' for ' : '';
      guestsLine = `${pluralize(count, partySizeUnit)}`;
      break;
    case 'TOGO_A_LA_CARTE':
      guestsLine = '';
      break;
  }

  return `${deliveryPickupPrefix}${forLine}${guestsLine}${purchaseOwnerText}`;
}

/**
 * Returns the maximum required age by the experience or any options on it that are contained within the given option orders
 * @param experience the ticket type which may have a required age
 * @param optionOrders any option orders on the ticket type which may have a required age
 * @returns the max required age to purchase this experience with the given options, or undefined if no age is required
 */
export function getRequiredAge(
  experience: core.TicketType,
  optionOrders: core.OptionOrder[] = []
): number | undefined {
  let requiredAge = 0;

  if (experience.requiredAge) {
    requiredAge = Math.max(requiredAge, experience.requiredAge);
  }

  const ticketOptionsById = _.keyBy(
    _.flatMap(experience.optionGroup ?? [], (optionGroup) => optionGroup.ticketOption ?? []),
    'id'
  );
  optionOrders.forEach((optionOrder) => {
    const ticketOption: core.ITicketOption | undefined = ticketOptionsById[optionOrder.optionId];
    if (ticketOption?.requiredAge) {
      requiredAge = Math.max(requiredAge, ticketOption?.requiredAge);
    }
  });

  return requiredAge > 0 ? requiredAge : undefined;
}

export function getRequiredPackageAge(
  experiences: core.TicketType[],
  purchaseOrders: core.PurchaseOrder[] = []
): number | undefined {
  return _.max(
    purchaseOrders
      .map((purchaseOrder) => {
        const experience = experiences.find(
          (experience) => experience.id === purchaseOrder.ticketTypeId
        );
        if (!experience) {
          return;
        }
        return getRequiredAge(experience, (purchaseOrder.optionOrder as core.OptionOrder[]) ?? []);
      })
      .filter((requiredAge) => !_.isNil(requiredAge))
  );
}

export function isPrimaryPatron(purchaseOrder: core.IPurchaseOrder, patron?: core.IPatron) {
  return !purchaseOrder.groupPrimaryPatron || patron?.id === purchaseOrder.groupPrimaryPatron.id;
}

/**
 * Returns a boolean if any refunds exist
 */
export function hasAnyRefunds(purchase: core.PastPurchase): boolean {
  return purchase.refund.length > 0;
}

/**
 * Returns a boolean if any money was involved in the past purchase
 */
export function hasAnyMoniesInvolved(purchase?: core.PastPurchase | core.PurchaseOrder): boolean {
  if (!purchase) {
    return false;
  }
  return (
    purchase.subtotal > 0 ||
    purchase.amountPaidCents > 0 ||
    purchase.netAmountPaidCents > 0 ||
    purchase.inStoreChargeCents > 0 ||
    purchase.deliveryServiceFeeCents > 0 ||
    !!(purchase as core.PurchaseOrder).paymentCard
  );
}

interface BuildPurchaseOrderOptions {
  selection: StoreType['selection'];
  lock: StoreType['lock'];
  ownerPatron?: core.IPatron;
  dinerPatron?: core.IPatron;
  gratuityRate?: number;
  promoCode?: string;
  giftCardDiscounts?: core.IDiscount[];
  customChargeOrder?: core.ICustomChargeOrder[];
  deliveryAddress?: DeliveryAddress;
  giftCard?: core.TockGiftCard;
  compAmountCents?: number;
  purchaseId?: number;
}

export function buildPurchaseOrder({
  compAmountCents,
  customChargeOrder = [],
  deliveryAddress,
  dinerPatron,
  giftCard,
  giftCardDiscounts = [],
  gratuityRate,
  lock,
  ownerPatron,
  promoCode,
  purchaseId,
  selection,
}: BuildPurchaseOrderOptions): core.IPurchaseOrder {
  const { currentLock } = lock;
  const ticketType = currentLock?.ticketType[0] as core.ITicketType;
  const ticketSubset: core.ITicketSubset = _.first(currentLock?.ticketSubset) || {};
  const purchaseVerifyToken =
    selection.purchaseVerifyToken ?? ticketSubset.purchaseVerifyInfo?.purchaseVerifyToken;
  const currentLockType = _.find(currentLock?.type, { id: ticketType.id });

  const purchaseOrder: core.IPurchaseOrder = {
    dateTime: ticketSubset.requestedDateTime,
    optionOrder: selection.options,
    ticketSubsetId: ticketSubset.id,
    ticketGroupId: currentLock?.id,
    ticketTypeId: ticketType.id,
    ticketTypePrice: currentLockType?.amount,
    ticketPriceInformation: ticketSubset.priceInformation,
    ticketCount: ticketSubset.ticketCount,
    fee: currentLock?.fee,
    seatingAreaId: ticketSubset.seatingArea?.id,
    seatingAreaName: ticketSubset.seatingArea?.name,
    ...deliveryAddress,
  };
  if (ticketType.variety === 'ORDER_DELIVERY' && deliveryAddress) {
    addDeliveryInfo(purchaseOrder, deliveryAddress, ownerPatron);
  }

  if (purchaseId) {
    purchaseOrder.basePurchaseId = purchaseId;
    purchaseOrder.compAmount = compAmountCents;
    purchaseOrder.customChargeOrder = [...customChargeOrder];
  }

  if (ownerPatron) {
    purchaseOrder.patron = ownerPatron;
  }

  if (dinerPatron) {
    purchaseOrder.dinerPatron = [dinerPatron];
  }

  if (purchaseVerifyToken) {
    purchaseOrder.purchaseVerifyToken = purchaseVerifyToken;
  } else if (selection.transferPurchaseToken) {
    purchaseOrder.transferPurchaseToken = selection.transferPurchaseToken;
  } else if (selection.visitEditToken) {
    purchaseOrder.visitEditToken = selection.visitEditToken;
    purchaseOrder.basePurchaseId = selection?.basePurchaseId;
  }

  if (
    safeEnumCompare(
      ticketType?.extraChargeDetails?.extraChargeType,
      ExtraChargeDetails.ExtraChargeType,
      ExtraChargeDetails.ExtraChargeType.GRATUITY
    )
  ) {
    purchaseOrder.selectedGratuityRate = gratuityRate;
  }

  purchaseOrder.discount = giftCardDiscounts;

  if (giftCard) {
    purchaseOrder.tockGiftCard = [giftCard];
  }

  if (promoCode) {
    purchaseOrder.discount.push({
      discountType: 'SUBTOTAL_COMP',
      description: 'Discount ' + promoCode,
      promoCode,
    });
  }

  return purchaseOrder;
}
